import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import "./ProjectSectionsPage.css";
import MapComponent from "../../Components/MapComponent/MapComponent";
import ProjectStatusPanelComponent from "../../Components/VerticalLegendComponent/VerticalLegendComponent";
import { CalendarMonth, KeyboardArrowLeft, InfoOutlined } from "@mui/icons-material";
import React, { useEffect, useState } from "react";
import StagesProgressComponent from "../../Components/StagesProgressComponent/StagesProgressComponent";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { getStageSections } from "../../Services/api";
import LoaderComponent from "../../Components/LoaderComponent/LoaderComponent";
import { formatMonthYear } from "../../Utils/date";
import QuarterlyReportsModal from "../../Components/QuarterlyReportsComponent/QuarterlyReportsModal";
import CompareQuartersComponent from "../../Components/CompareQuartersComponents/CompareQuartersComponent";
interface Props { }

const ProjectSectionsPage = (props: Props) => {
  const { id, stageIndex } = useParams();
  const { hideDelays, hideLegend, stages, project } = useOutletContext<any>();

  // const [stageSections, setStageSections] = useState<any>(null);
  const [stageSections, setStageSections] = useState<any[]>([]);

  const [selectedStageIndex, setSelectedStageIndex] = useState(0);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);

  const [subLoading, setSubLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showQuarterlyModal, setShowQuarterlyModal] = useState(false);

  const { setBreadcrumbs, setBackButton } = useOutletContext<any>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const selectedStage = stages[selectedStageIndex];
  const selectedSection = stageSections[selectedSectionIndex] || null;
  const selectedSerial = selectedSection?.serial || selectedStage?.serial;

  const navigate = useNavigate();

  useEffect(() => {
    setSelectedStageIndex(Number(stageIndex));
    setBreadcrumbs([
      { name: 'לו"ז המיזם', path: "/project/details/" + id },
      { name: "מקטעים", path: "#" },
    ]);
    setBackButton({ title: 'לו"ז המיזם', route: `/project/details/${id}` });

    getStageSections(id, stages[Number(stageIndex)].id_number)
      .then((sectionsData) => {
        setStageSections(sectionsData.sections);
        setSubLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
      })
      .finally(() => { });

    const resetFocus = document.getElementById("reset-focus");
    if (resetFocus) resetFocus.focus();
  }, [id, setBreadcrumbs, setBackButton]);

  const loadSections = async (stageIndex: number, stageId: number) => {
    setSubLoading(true);
    setError(null);

    getStageSections(id, stageId)
      .then((data) => {
        if (data.sections.length === 0) {
          navigate("/project/details/" + id, { state: { selectedStageIndex: stageIndex } });
        }
        setStageSections(data.sections);
        setSubLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Unknown error");
      })
      .finally(() => { });
  };

  // if (loading) return <LoaderComponent/>;
  // if (error) return <p>Error: {error}</p>;

  const onChangeStage = (index: number) => {
    setSelectedStageIndex(index);
    setSelectedSectionIndex(0);
    loadSections(Number(index), stages[index].id_number);
  };

  const onChangeStageSection = (index: number) => {
    setSelectedSectionIndex(index);
  };

  return (
    <Box>
      <Grid container spacing={2}>
        {(!hideLegend || !isMobile) && (
          <Grid size={{ xs: 12, md: 2 }}>
            <ProjectStatusPanelComponent />
          </Grid>
        )}
        <Grid size={{ xs: 12, md: 8 }}>
          <StagesProgressComponent stages={stages} selectedIndex={selectedStageIndex} hideDelays={hideDelays} onChangeStage={onChangeStage} />
        </Grid>
      </Grid>

      <Typography variant={"h2"} textAlign={"center"} mb={3} className="project-details-breadcrumbs">
        מקטעים של שלב {stages[selectedStageIndex].name}
      </Typography>

      {subLoading || !stages?.[selectedStageIndex] || !stageSections?.length || !stageSections?.[selectedSectionIndex] ? (
        <LoaderComponent fullScreen={false} />
      ) : (
        <div className="project-sections-container">
          <Grid container spacing={2} mb={10} justifyContent="center">
            <Grid container spacing={2} size={10}>
              <Grid size={12}>
                <StagesProgressComponent
                  stages={stageSections}
                  selectedIndex={selectedSectionIndex}
                  hideDelays={hideDelays}
                  onChangeStage={onChangeStageSection}
                  compact={true}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: 2,
                    p: 3,
                    minHeight: "100%",
                    boxSizing: "border-box",
                  }}
                  dir="rtl"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                      mb: 2,
                    }}
                  >
                    <InfoOutlined sx={{ fontSize: 24, color: "#0C3768", ml: 1 }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0C3768" }}>תיאור המקטע</Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#0C3768",
                      lineHeight: 1.6,
                      whiteSpace: "pre-line", // preserves line breaks if your text has any
                    }}
                  >
                    {stageSections[selectedSectionIndex].description}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box sx={{ bgcolor: "#FFFFFF", borderRadius: 2, p: 3 }} dir="rtl">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "right",
                      mb: 2,
                    }}
                  >
                    <CalendarMonth sx={{ fontSize: 24, color: "#0C3768", ml: 1 }} />
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0C3768" }}>תאריכי התחלה וסיום</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0C3768", textAlign: "right" }}>
                    תאריך סיום עדכני: {formatMonthYear(stageSections[selectedSectionIndex].updated_end_date)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0C3768",
                      textAlign: "right",
                      mb: 1,
                    }}
                  >
                    תאריך התחלה עדכני: {formatMonthYear(stageSections[selectedSectionIndex].updated_start_date)}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 400, color: "#0C3768", textAlign: "right" }}>
                    תאריך סיום מקורי: {formatMonthYear(stageSections[selectedSectionIndex].original_end_date)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: "#0C3768",
                      textAlign: "right",
                      mb: 2,
                    }}
                  >
                    תאריך התחלה מקורי: {formatMonthYear(stageSections[selectedSectionIndex].original_start_date)}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  sx={{
                    bgcolor: "#FFFFFF",
                    borderRadius: "15px",
                    padding: "10px",
                    boxSizing: "border-box",
                    height: "100%",
                  }}
                >
                  <MapComponent
                    layerNumber={(() => {
                      return stageSections[selectedSectionIndex]?.serial;
                    })()}
                    activeLayerNumber={stageSections[selectedSectionIndex]?.project_layer}
                  />
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  tabIndex={0}
                  role="button"
                  onClick={() => setShowQuarterlyModal(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setShowQuarterlyModal(true);
                    }
                  }}
                  sx={{ bgcolor: "#FFFFFF", borderRadius: 2, p: 2, cursor: "pointer" }} dir="rtl" >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "right" }}>
                      <InfoOutlined sx={{ fontSize: 24, color: "#0C3768", ml: 1 }} />
                      <Box >
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0C3768" }}>להשוואה בין הדיווחים במקטע זה</Typography>

                        <Typography
                          sx={{
                            display: "block",
                            fontSize: 14,
                            fontWeight: 400,
                            color: "#0C3768",
                          }}
                        >
                          יש עיכוב של כ-{stageSections[selectedSectionIndex].days_late} ימים.
                        </Typography>
                      </Box>
                    </Box>
                    <KeyboardArrowLeft sx={{ fontSize: 24, color: "#0C3768", ml: 1, opacity: "30%" }} />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {stageSections[selectedSectionIndex].has_children && (
            <div className="project-details-goto-sections">
              <Link
                state={{ sections: stageSections }}
                style={{ textDecoration: "none", color: "inherit", cursor: "pointer" }}
                to={`/project/sub-sections/${id}/${selectedStageIndex}/${selectedSectionIndex}`}
              >
                <Typography fontSize={22} fontWeight={700}>
                  לצפייה בתתי המקטעים של {stageSections[selectedSectionIndex].name}
                </Typography>
                <img src="/images/arrow_down.png" alt="up arrow" className="sticky-arrow" />
              </Link>
            </div>
          )}
          {/* כאן הקומפוננטה שנפתחת בלחיצה */}
          {stages?.[selectedStageIndex]?.serial && stageSections?.[selectedSectionIndex]?.serial && (
            <QuarterlyReportsModal
              open={showQuarterlyModal}
              onOpenChange={setShowQuarterlyModal}
              projectId={id!}
              serialNumber={selectedSerial}
              type="section"
            />
            // Same component but the xlsx fille is difrecess between quaters
            // <CompareQuartersComponent
            //     open={showQuarterlyModal}
            //      onOpenChange={setShowQuarterlyModal}
            //      projectId={7}
            //      serialNumber={selectedSerial}
            // />
          )}
        </div>
      )}
    </Box>
  );
};

export default ProjectSectionsPage;
